import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { Alert, Collapse, Grid, List, ListItem, ListItemButton, ListItemText, Popover } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// custom utils
import { DrawChart } from "../charts/TVChart";
import { useWindowDimensions } from "../pages/Utils";

const NoListAlert = ({ openAlert, setOpenAlert }) => (
  <Collapse in={openAlert}>
    <Alert
      action={
        <IconButton
          aria-label="close"
          color="inherit"
          size="small"
          onClick={() => setOpenAlert(false)}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      }
      sx={{ mb: 1 }}
      severity="info"
    >
      <h6>
        You are viewing default watchlists. To create a custom watchlist visit the{" "}
        <span style={{ color: "blue" }}>MAKE</span> tab.
      </h6>
    </Alert>
  </Collapse>
);

const UserlistWatch = ({
  isAuthenticated,
  user,
  navbarSize,
  topbarSize,
  userWatchlist,
  publicWatchlist,
  interval,
  darkMode,
}) => {
  const [loading, setLoading] = useState(false);

  // merged watchlist state
  const [userList, setUserList] = useState([]);
  const [listNames, setListNames] = useState([]);
  const [activeListName, setActiveListName] = useState("");
  const [activeListItems, setActiveListItems] = useState([]);
  const [symbol, setSymbol] = useState("");

  // choose user or public watchlist
  useEffect(() => {
    let wl = [];
    if (userWatchlist.length > 0) {
      wl = userWatchlist;
    } else {
      wl = publicWatchlist.slice(0, 2);
    }
    setUserList(wl);
  }, [userWatchlist, publicWatchlist]);

  // derive names + items when list changes
  useEffect(() => {
    if (userList.length > 0) {
      const names = userList.map((l) => l.Name || l.listname || "");
      const firstItems = Array.isArray(userList[0]?.items)
        ? userList[0].items
        : Array.isArray(userList[0]?.Results)
        ? userList[0].Results
        : [];
      const items = firstItems.map((i) => i.Symbol || i.symbol || "");

      setListNames(names);
      setActiveListName(names[0]);
      setActiveListItems(items);
      setSymbol(items[0] || "");
    }
  }, [userList]);

  const handleWatchlist = (name) => {
    const found = userList.find((v) => v.Name === name || v.listname === name);
    if (!found) {
      alert("This is a default watchlist. To create custom watchlists, visit Make page.");
      return;
    }

    const items = Array.isArray(found.items)
      ? found.items
      : Array.isArray(found.Results)
      ? found.Results
      : [];

    const li = items.map((i) => i.Symbol || i.symbol || "");
    setActiveListName(name);
    setActiveListItems(li);
    setSymbol(li[0] || "");
  };

  // chevron toggle
  const [collapsed, setCollapsed] = useState(false);
  const togglePanel = () => {
    setCollapsed(!collapsed);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimeout = useRef();

  const handleHoverOpen = (event) => {
    if (collapsed) {
      clearTimeout(closeTimeout.current);
      setAnchorEl(event.currentTarget);
    }
  };
  const handleHoverClose = () => {
    // Set a timeout to close the popover, allowing the cursor to move to it
    closeTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 200);
  };

  const handlePopoverEnter = () => {
    // When the mouse enters the popover, clear the timeout to keep it open
    clearTimeout(closeTimeout.current);
  };

  const handlePopoverLeave = () => {
    // When the mouse leaves the popover, close it immediately
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // dimensions
  const { width, height } = useWindowDimensions();
  const topHeight = navbarSize + 10;

  // alert toggle
  const [openAlert, setOpenAlert] = useState(userWatchlist.length === 0);

  return (
    <div>
      {loading ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <Grid
          container
          sx={{
            minHeight: height - topHeight,
            // backgroundColor: darkMode ? "#121212" : "darkgrey",
            backgroundColor: "darkgrey",
          }}
        >
          {/* Main chart */}
          <Grid
            item
            sx={{
              flex: 1,
              height: height - topHeight,
            }}
          >
            <DrawChart
              width="100%"
              height={height - topHeight}
              symbol={symbol}
              watchlist={activeListItems}
              interval={interval}
              darkMode={darkMode}
            />
          </Grid>
          {/* Side panel */}
          <Grid
            item
            sx={{
              width: collapsed ? "25px" : { lg: "20%", xl: "10%" },
              height: height - topHeight,
              overflowY: collapsed ? "hidden" : "auto",
              overflowX: "hidden",
              transition: "all 0.3s ease",
              position: "relative",
              backgroundColor: "darkgrey",
            }}
          >
            {/* Collapse/Expand button */}
            <IconButton
              onClick={togglePanel}
              onMouseEnter={handleHoverOpen}   // 👈 show list on hover
              onMouseLeave={handleHoverClose}  // 👈 hide when leaving
              size="large"
              sx={{
                position: "absolute",
                top: 5,
                left: -15,
                zIndex: 10,
                backgroundColor: "white",
                border: "1px solid rgba(0,0,0,0.2)",
                "&:hover": { backgroundColor: "lightgrey" },
              }}
            >
              {collapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handlePopoverLeave}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              disableRestoreFocus
              sx={{ pointerEvents: "none" }}   // 👈 prevents flicker
              PaperProps={{
                  onMouseEnter: handlePopoverEnter,
                  onMouseLeave: handlePopoverLeave,
                  // Offset to counteract the IconButton's position and align with the panel
                  style: { pointerEvents: "auto", marginLeft: '15px' },
              }}
            >
              <List sx={{ paddingTop: 0 }}>
                {listNames.map((name) => (
                  <ListItem key={name} disablePadding sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.15)" }}>
                    <ListItemButton selected={activeListName === name} onClick={() => handleWatchlist(name)} sx={{ "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "lightgrey" } }}>
                      <ListItemText primary={name} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Popover>

            {!collapsed && (
              <>
                <NoListAlert openAlert={openAlert} setOpenAlert={setOpenAlert} />

                <List sx={{ paddingTop: 0 }}>
                  {listNames.map((name) => (
                    <ListItem key={name} disablePadding sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.15)" }}>
                      <ListItemButton selected={activeListName === name} onClick={() => handleWatchlist(name)} sx={{ "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: "lightgrey" } }}>
                        <ListItemText primary={name} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </Grid>
        </Grid>
      )}
    </div>
  );
};

const mapStateToProps = (state) => {
  let userWatchlist = [];
  const wl = state.auth.watchlistData;
  if (wl && wl.data) {
    userWatchlist = wl.data;
  } else if (Array.isArray(wl)) {
    userWatchlist = wl;
  }
  return {
    isAuthenticated: state.auth.isAuthenticated,
    user: state.auth.user,
    navbarSize: state.barSize.navbarSize,
    topbarSize: state.barSize.topbarSize,
    userWatchlist,
    publicWatchlist: state.publicList.publicList,
    interval: state.settings.interval,
    darkMode: state.settings.darkMode,
  };
};

export default connect(mapStateToProps)(UserlistWatch);
